import { InflateRequestMode } from '@sccs/types';
import { ApolloClient } from 'sccs-shared-apollo-client-v0';
import { RequestHeaderConfiguration } from '../../../../types/ApiClientConfig';
import { MarketConfiguration, SharedRequestConfiguration } from '../../../../types/GeneralParameter';
import { OptionalOneSearchGraphQlResponseWithRawData } from '../../../../types/GeneralTypes';
import { Query, QuerySearchArgs } from '../../../../types/one-search/queries';
import { splitModelIdentifier } from '../../../../util/SplitData';
import { isCommercialVanBy, isVanBy } from '../../../../util/VansConfiguration';
import {
  getRequestGraphQLConfiguration,
  removeClassSuffixForOneSearch,
  removeClassSuffixFromMultipleIdentifier,
} from '../../../../util/one-search/RequestConfiguration';
import {
  filterBodyTypes,
  filterFuelTypeHarmonized,
  filterVehicleClasses,
} from '../../../../util/one-search/VehicleClassTypeGuard';
import { ModeSpecificFetchClient } from '../../../GenericFetchClient';
import { GET_FACETS, GET_SEARCH_RESULTS } from './queries';
export type OneSearchClient = ApolloClient;

export type OneSearchTileDataResponseWithId = Query & {
  responseTypeId: 'OneSearchVehicleTileDataQuery';
};

export type OneSearchVehicleTileDataParams = Omit<QuerySearchArgs, 'modelIdentifier' | 'bodyType' | 'fuelType'> &
  MarketConfiguration & {
    modelIdentifier?: string[];
    typeClass?: string[];
    bodyType?: string[];
    fuelType?: string[];
  };

export class OneSearchGraphQlClient implements ModeSpecificFetchClient {
  private parameters?: QuerySearchArgs;
  private config?: RequestHeaderConfiguration;
  private sharedConfiguration?: SharedRequestConfiguration;
  constructor(config: RequestHeaderConfiguration) {
    this.config = config;
  }

  getInflateIdLength(): number {
    throw new Error('Method not implemented.');
  }
  setMode(): void {
    throw new Error('Method not implemented.');
  }

  setRequiredParameters(parameters: OneSearchVehicleTileDataParams, configuration: SharedRequestConfiguration): void {
    const additionalRequestConfig = this.getAdditionalRequestConfig(parameters, configuration);
    const graphQlParamters: QuerySearchArgs = {
      ...additionalRequestConfig,
      language: parameters.lang,
      modelIdentifier: filterVehicleClasses(additionalRequestConfig.modelIdentifier ?? []),
      bodyType: filterBodyTypes(additionalRequestConfig.bodyType ?? []),
      fuelType: filterFuelTypeHarmonized(additionalRequestConfig.fuelType ?? []),
    };
    this.parameters = graphQlParamters;
    this.sharedConfiguration = configuration;
  }

  private getAdditionalRequestConfig(
    parameter: OneSearchVehicleTileDataParams,
    configuration: SharedRequestConfiguration,
  ) {
    if (configuration.mode === InflateRequestMode.SIMPLE_PRICE) {
      const modelIdentifierWithBodyType = parameter.modelIdentifier?.[0] ?? 'NONE';
      const modelAndBody = splitModelIdentifier(modelIdentifierWithBodyType);
      const classFilters = this.getClassFilterByMode(configuration.mode, [modelAndBody.model]);
      const bodyType = modelAndBody?.body;
      const isVan = isCommercialVanBy(modelAndBody.model);
      return getRequestGraphQLConfiguration(isVan, configuration, {
        ...parameter,
        modelIdentifier: classFilters,
        bodyType: bodyType ? [bodyType] : [],
      });
    } else {
      const classFilters = this.getClassFilterByMode(configuration.mode, parameter.modelIdentifier ?? []);
      const isVan = isVanBy(configuration.mode);

      return getRequestGraphQLConfiguration(isVan, configuration, { ...parameter, modelIdentifier: classFilters });
    }
  }

  private getClassFilterByMode(mode: string, classFilter: string[]): string[] {
    if (classFilter.length < 1) return [];
    switch (mode) {
      case InflateRequestMode.PI_PAGES_VANS:
        return classFilter; // VITO_TOURER won't be splitted here
      case InflateRequestMode.PI_PAGES_CARS:
      case InflateRequestMode.SIMPLE_PRICE:
      case InflateRequestMode.BANNER_CAMPAIGN: {
        const modelIdentifierArray = classFilter.map((modelId) => removeClassSuffixForOneSearch(modelId));
        return modelIdentifierArray;
      }
      case InflateRequestMode.HOMEPAGE_CARS:
      case InflateRequestMode.HOMEPAGE_VANS:
      case InflateRequestMode.CAMPAIGN_TEASER_CARS:
      case InflateRequestMode.CAMPAIGN_TEASER_VANS: {
        return [removeClassSuffixFromMultipleIdentifier(classFilter?.[0])];
      }

      default:
        throw new Error('Unknown Teaser Mode');
    }
  }

  async getResponse(): Promise<OptionalOneSearchGraphQlResponseWithRawData> {
    const { ApolloClient, createHttpLink, InMemoryCache } = await import('sccs-shared-apollo-client-v0');
    const httpLink = createHttpLink({
      uri: this.config?.basePath,
      headers: {
        'x-api-key': this.config?.apiKey || '',
      },
    });

    const cache = new InMemoryCache({});

    const oneSearchClient = new ApolloClient({
      link: httpLink,
      cache,
    });
    const gqlParams = await this.getParams(this.sharedConfiguration?.mode ?? '', oneSearchClient, this.parameters);

    const gqlQuerySearchResultParams = {
      variables: {
        ...gqlParams,
      },
      query: GET_SEARCH_RESULTS,
    };

    const oneSearchResponse = await oneSearchClient.query<Query>({
      ...gqlQuerySearchResultParams,
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    return {
      responseTypeId: 'OneSearchGraphQlWithRawData',
      search: {
        results: oneSearchResponse?.data?.search?.results || [],
      },
    };
  }

  private async getParams(
    mode: string,
    client: ApolloClient,
    parameter?: QuerySearchArgs,
  ): Promise<QuerySearchArgs | undefined> {
    if (!parameter) return undefined;
    switch (mode) {
      case InflateRequestMode.PI_PAGES_VANS:
      case InflateRequestMode.PI_PAGES_CARS:
      case InflateRequestMode.HOMEPAGE_CARS:
      case InflateRequestMode.HOMEPAGE_VANS:
      case InflateRequestMode.SIMPLE_PRICE:
      case InflateRequestMode.ZERO_RESULT:
      case InflateRequestMode.BANNER_CAMPAIGN:
        return { ...parameter };
      case InflateRequestMode.CAMPAIGN_TEASER_CARS:
      case InflateRequestMode.CAMPAIGN_TEASER_VANS: {
        const gqlQueryFacetsParams = {
          variables: {
            ...this.parameters,
          },
          query: GET_FACETS,
        };

        const facetsResponse = await client.query<Query>({
          ...gqlQueryFacetsParams,
          errorPolicy: 'all',
          fetchPolicy: 'no-cache',
        });
        const campaign = parameter.campaigns as string[];
        // onesearch Gql only filters the vehicles with the hashed value,
        // Therefore we need to do a request to get the hashed value and then
        // a second request to get filtered vehicles.
        const hashedCampaign =
          (facetsResponse?.data?.search?.facets?.campaigns?.values?.filter((facet) => {
            return facet.formattedValue === campaign[0];
          })[0]?.value as string) ?? '';

        return {
          ...parameter,
          campaigns: [hashedCampaign],
        };
      }

      default:
        throw new Error('Unknown Teaser Mode');
    }
  }

  checkAllRequiredParametersPresent(): boolean {
    return true;
  }
}
